import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { SlidersHorizontal, Utensils, X as XIcon, ExternalLink } from 'lucide-react';
import api, { errorMessage, toPage } from '../services/api';
import {
  PageHeader,
  Card,
  Segmented,
  SearchInput,
  Pagination,
  Sheet,
  Button,
  Badge,
  Field,
  InfoRow,
  CopyButton,
  ErrorBanner,
  SkeletonList,
  EmptyState,
  ResponsiveList,
  inputClass,
  textareaClass,
  useDebounce,
  cx
} from '../components/ui';
import { formatCurrency, formatDateTime } from '../utils/format';

const STATUS_OPTIONS = ['Đang xử lý', 'Ghi nhận không thành công', 'Chờ duyệt tiền', 'Duyệt thành công'];

const STATUS_TONE = {
  'Duyệt thành công': 'green',
  'Chờ duyệt tiền': 'yellow',
  'Ghi nhận không thành công': 'red',
  'Đang xử lý': 'blue'
};

const StatusBadge = ({ status }) => <Badge tone={STATUS_TONE[status] || 'gray'}>{status || '—'}</Badge>;

const ShopeeFoodPage = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ commission: '', status: '', note: '' });
  const [saving, setSaving] = useState(false);
  const debouncedSearch = useDebounce(search);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['shopeefood', debouncedSearch, status, dateFrom, dateTo, page, limit],
    async () => {
      const params = { page, limit };
      if (debouncedSearch) params.search = debouncedSearch;
      if (status) params.status = status;
      if (dateFrom) params.start_date = dateFrom;
      if (dateTo) params.end_date = dateTo;
      return toPage(await api.get('/admin/shopeefood-orders', { params }));
    },
    { keepPreviousData: true }
  );

  const orders = data?.items || [];
  const pagination = data?.pagination || {};
  const hasDate = !!(dateFrom || dateTo);

  const openEdit = (order) => {
    setEditing(order);
    setForm({ commission: order.commission ?? '', status: order.status || STATUS_OPTIONS[0], note: order.note || '' });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { status: form.status, note: form.note };
      if (form.commission !== '') payload.commission = form.commission;
      await api.patch(`/admin/shopeefood-orders/${editing.id}`, payload);
      toast.success('Đã cập nhật đơn ShopeeFood');
      setEditing(null);
      refetch();
      queryClient.invalidateQueries('dashboard');
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi khi cập nhật đơn hàng'));
    } finally {
      setSaving(false);
    }
  };

  const renderCard = (o) => (
    <Card as="button" type="button" onClick={() => openEdit(o)} className="w-full p-4 text-left active:bg-gray-50">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-mono text-sm font-semibold text-gray-900">{o.spf_id}</p>
          <p className="truncate text-xs text-gray-500">{o.user?.name || '—'} · {o.user?.email || '—'}</p>
        </div>
        <StatusBadge status={o.status} />
      </div>
      {o.note && <p className="mt-2 line-clamp-2 text-xs text-gray-600">Lý do: {o.note}</p>}
      <div className="mt-3 flex items-end justify-between">
        <span className="text-xs text-gray-400">{formatDateTime(o.created_at)}</span>
        <span className="text-base font-semibold text-gray-900">{o.commission != null ? formatCurrency(o.commission) : '—'}</span>
      </div>
    </Card>
  );

  const columns = [
    { key: 'id', title: 'Mã đơn', render: (o) => <span className="font-mono font-medium">{o.spf_id}</span> },
    {
      key: 'user',
      title: 'Người dùng',
      render: (o) => (
        <div>
          <p className="font-medium text-gray-900">{o.user?.name || '—'}</p>
          <p className="text-xs text-gray-500">{o.user?.email || '—'}</p>
        </div>
      )
    },
    { key: 'commission', title: 'Hoa hồng', align: 'right', render: (o) => (o.commission != null ? formatCurrency(o.commission) : '—') },
    { key: 'status', title: 'Trạng thái', render: (o) => <StatusBadge status={o.status} /> },
    { key: 'note', title: 'Lý do', tdClass: 'max-w-xs', render: (o) => <span className="line-clamp-2 text-xs text-gray-600">{o.note || '—'}</span> },
    { key: 'created', title: 'Ngày tạo', render: (o) => <span className="text-xs">{formatDateTime(o.created_at)}</span> }
  ];

  return (
    <div>
      <PageHeader title="ShopeeFood" subtitle={`${pagination.total ?? 0} đơn`} />

      <div className="filter-bar">
        <div className="flex gap-2 lg:min-w-[18rem] lg:flex-1">
          <SearchInput className="flex-1" value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Tìm theo mã đơn (SPF ID)" />
          <Button variant={hasDate ? 'soft' : 'outline'} size="icon" className="h-11 w-11 flex-shrink-0 md:h-9 md:w-9" onClick={() => setShowFilters(true)} aria-label="Lọc ngày">
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </div>
        <Segmented
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); }}
          options={[{ value: '', label: 'Tất cả' }, ...STATUS_OPTIONS.map((s) => ({ value: s, label: s }))]}
        />
        {hasDate && (
          <Badge className="gap-1 py-1">
            {dateFrom || '…'} → {dateTo || '…'}
            <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }} aria-label="Bỏ lọc ngày"><XIcon className="h-3.5 w-3.5" /></button>
          </Badge>
        )}
      </div>

      <ErrorBanner message={error ? 'Lỗi khi tải danh sách đơn ShopeeFood' : ''} onRetry={refetch} />

      {isLoading ? (
        <SkeletonList />
      ) : (
        <ResponsiveList
          items={orders}
          renderCard={renderCard}
          columns={columns}
          onRowClick={openEdit}
          empty={<EmptyState icon={Utensils} title="Không có đơn ShopeeFood" />}
        />
      )}

      {pagination.total > 0 && (
        <Pagination
          page={page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          onChange={setPage}
          limit={limit}
          onLimitChange={(n) => { setLimit(n); setPage(1); }}
          disabled={isFetching}
        />
      )}

      <Sheet
        open={showFilters}
        onClose={() => setShowFilters(false)}
        title="Lọc theo ngày tạo"
        footer={<Button className="w-full" onClick={() => { setPage(1); setShowFilters(false); }}>Áp dụng</Button>}
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Từ ngày"><input type="date" className={inputClass} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Field>
          <Field label="Đến ngày"><input type="date" className={inputClass} value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Field>
        </div>
      </Sheet>

      <Sheet
        open={!!editing}
        onClose={() => !saving && setEditing(null)}
        title={editing ? `Đơn ${editing.spf_id}` : ''}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Hủy</Button>
            <Button onClick={save} loading={saving}>Lưu</Button>
          </div>
        }
      >
        {editing && (
          <div className="space-y-4">
            <Card className="divide-y divide-gray-100 px-4">
              <InfoRow label="Người dùng">{editing.user?.email || '—'}</InfoRow>
              <InfoRow label="Tạo lúc">{formatDateTime(editing.created_at)}</InfoRow>
              {editing.short_link && (
                <InfoRow label="Link">
                  <span className="inline-flex items-center gap-1">
                    <a href={editing.short_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gray-900 underline underline-offset-2">
                      Mở <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <CopyButton value={editing.short_link} />
                  </span>
                </InfoRow>
              )}
            </Card>
            <Field label="Trạng thái">
              <div className="grid grid-cols-2 gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, status: s }))}
                    className={cx(
                      'min-h-[44px] rounded-xl border px-3 py-2 text-left text-sm',
                      form.status === s ? 'border-gray-900 bg-gray-100 font-medium text-gray-900' : 'border-gray-200 text-gray-600'
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Hoa hồng (VND)">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                className={inputClass}
                value={form.commission}
                onChange={(e) => setForm((f) => ({ ...f, commission: e.target.value }))}
                placeholder="VD: 15000"
              />
            </Field>
            <Field label="Lý do / ghi chú">
              <textarea
                rows={3}
                className={textareaClass}
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Nhập lý do..."
              />
            </Field>
          </div>
        )}
      </Sheet>
    </div>
  );
};

export default ShopeeFoodPage;
